import { useFormContext } from 'react-hook-form';
// eslint-disable-next-line no-restricted-imports -- 기존 결제 재시도 모달이 NiceModal 기반입니다. overlay-kit 전환은 별도 리팩토링에서 처리합니다.
import { useModal } from '@ebay/nice-modal-react';
import { isAxiosError } from 'axios';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';

import { useDialog } from '@zep/dialog';

import {
  useRemovePaymentCard,
  useSubscriptionPlan,
  useUpdateZipCode,
} from '../../../../../services';
import { fDateByCountryCode } from '../../../../../utils';
import { pushGtmPurchaseEvent } from '../../../../../utils/gtmMarketingEvent';
import { useGoSignin } from '../../hooks/useGoSignin';
import { capitalizeFirstLetter } from '../../subscriptionUtil';
import { PaymentRetryDialog } from '../modals/PaymentRetryDialog';

import {
  PlanCheckoutFormSchemaType,
  useSubscriptionPaymentContext,
} from './index';

const FAIL_TO_BILLING_PAYMENT_BY_PG = -158;
const FAIL_TO_EDIT_BILLING = -157;
const FAIL_TO_CREATE_BILLING = -155;
const FAIL_TO_EDIT_ZIP_CODE = -211;
export const findSubscriptionError = (
  e: any,
): {
  type:
    | '401'
    | 'PG_ERROR'
    | 'ZEP_API_ERROR'
    | 'UNKNOWN_ERROR'
    | 'FAIL_TO_EDIT_ZIP_CODE';
  message: string;
} => {
  if (e.code === 'ECONNABORTED') {
    return { type: 'ZEP_API_ERROR', message: e.message };
  }

  // @ts-ignore
  if (isAxiosError(e) && e.response.status === 401) {
    return {
      type: '401',
      message: 'subscription:payment.alertFailPay.descriptionLogOut',
    };
  }

  // @ts-ignore
  const findErrorKey = _e => {
    if (_e.code) {
      return _e.code;
    }
    if (_e.statusKey) {
      return _e.statusKey;
    }
    return _e.status;
  };

  // ZEP ERROR
  if (e.status < 0) {
    const errorCode = +e.status;
    if (
      errorCode === FAIL_TO_BILLING_PAYMENT_BY_PG ||
      errorCode === FAIL_TO_CREATE_BILLING ||
      errorCode === FAIL_TO_EDIT_BILLING
    ) {
      return {
        type: 'PG_ERROR',
        message: e?.message ?? `${findErrorKey(e)}`,
      };
    }

    if (errorCode === FAIL_TO_EDIT_ZIP_CODE) {
      return {
        type: 'FAIL_TO_EDIT_ZIP_CODE',
        message: e?.statusKey,
      };
    }

    return {
      type: 'ZEP_API_ERROR',
      message: `${findErrorKey(e)}`,
    };
  }

  return {
    type: 'UNKNOWN_ERROR',
    message: 'An error occurred',
  };
};

export const usePaymentAction = () => {
  const dialog = useDialog();
  const router = useRouter();
  const moveSignIn = useGoSignin();
  const { t } = useTranslation(['subscription']);

  const { getValues } = useFormContext<PlanCheckoutFormSchemaType>();
  const paymentRetryDialog = useModal(PaymentRetryDialog);

  const { mutateAsync: _mutateSubscriptionPlan } = useSubscriptionPlan();
  const { mutateAsync: removePaymentCard } = useRemovePaymentCard();
  const { mutateAsync: mutateZipCode } = useUpdateZipCode();

  const {
    amplitudeData,
    추가결제금액,
    space,
    selectedMaxCcu,
    selectedPlan,
    selectedPlanInfo,
    isUpdated,
    spaceHashId,
    updateStatus,
    spacePlan,
    subscriptionPeriod,
    subscriptionRequestHashId,
  } = useSubscriptionPaymentContext();

  const moveCompletePage = (orderId?: string) => {
    const generateUpdateMessage = () => {
      switch (updateStatus.type) {
        case 'DOWN_GRADE': {
          // @ts-ignore
          const endDate = new Date(spacePlan.endDate);

          const letter = capitalizeFirstLetter;
          const dateString = fDateByCountryCode(endDate, space.countryCode);
          return selectedMaxCcu < 0
            ? t('subscription:checkout.complete.infiniteUpdateMessage', {
                date: dateString,
                spaceName: space.name,
                plan: letter(selectedPlan),
              })
            : t('subscription:payment.complete.downgradeMessage', {
                date: dateString,
                spaceName: space.name,
                ccu: selectedMaxCcu,
                plan: letter(selectedPlan),
              });
        }
        default: {
          return updateStatus.messages[updateStatus.messages.length - 1]; // 마지막 메세지만
        }
      }
    };

    const { entryMapHashId, thumbnailUrl, hashId } = space;
    const completeTitle = (() => {
      // @ts-ignore
      if (updateStatus.type === 'UP_GRADE' && 추가결제금액.value <= 0) {
        return t('subscription:payment.complete.downgradeTitle');
      }
      switch (updateStatus.type) {
        case 'UP_GRADE': {
          return t('subscription:payment.complete.upgradeTitle');
        }
        case 'DOWN_GRADE': {
          return t('subscription:payment.complete.downgradeTitle');
        }
        default: {
          return t('subscription:payment.complete.defaultTitle');
        }
      }
    })();
    const updateMessages = generateUpdateMessage();

    router.push(
      {
        pathname: '/checkout/complete',
        query: {
          ...router.query,
          orderId,
          thumbnailUrl,
          entryMapHashId,
          title: completeTitle,
          spaceHashId: hashId,
          spaceName: space.name,
          plan: selectedPlan as string,
          maxCcu: selectedMaxCcu,
          updateStatus: updateStatus.type,
          updateMessages,
          amplitudeData: JSON.stringify(amplitudeData),
          subscriptionPeriod,
        },
      },
      '/checkout/complete',
    );
  };

  const subscribePlan = async (
    _payload: { retry: () => void },
    useRemoveBillingKey = true,
  ) => {
    const { retry } = _payload;

    try {
      // Promise.all 을 사용하려 했으나 결제가 성공해도 우편번호 업데이트가 실패하면 결제 자체를 재시도할 우려가 있어 순차적으로 처리
      if (!(space.countryCode === 'KR' || space.countryCode === 'JP')) {
        await mutateZipCode({
          spaceHashId,

          // @ts-ignore
          zipCode: getValues('zipCode') === '' ? null : getValues('zipCode'),
        });
      }
      const { data } = await _mutateSubscriptionPlan({
        spacePlanTemplateId: selectedPlanInfo.id,
        isUpdated,
        spaceHashId,
        countryCode: space.countryCode,
        subscriptionRequestHashId,
      });

      // 마케팅 전환 추적용 purchase 신호 (GTM Custom Event 트리거로 소비).
      // 사용자가 조작 가능한 URL query가 아니라, 서버가 결제를 확인한 응답
      // (payment.orderId)과 결제 컨텍스트의 실결제액(amplitudeData.price,
      // VAT 포함·업그레이드 시 추가결제금액)을 사용한다.
      // 즉시 결제가 없는 플로우(다운그레이드 등)는 payment가 없어 제외된다.
      if (data.payment?.orderId) {
        // 통화는 결제 대상 플랜의 서버 통화를 우선 사용하고, 신규 구독처럼
        // spacePlan이 없으면 국가 기준으로 결정한다 (괄호로 우선순위 명시).
        const chargedCurrency =
          spacePlan?.currency ?? (space.countryCode === 'KR' ? 'KRW' : 'USD');
        pushGtmPurchaseEvent({
          value: amplitudeData.price,
          currency: chargedCurrency,
          transactionId: data.payment.orderId,
        });
      }

      moveCompletePage(data.payment?.orderId);
    } catch (e) {
      const title = t('subscription:payment.alertFailPay.title');
      const { type, message } = findSubscriptionError(e);

      switch (type) {
        case '401': {
          await dialog.alert({
            title,

            // @ts-ignore
            content: t[message],
            useOutSideClick: false,
          });
          moveSignIn(true);
          break;
        }
        case 'PG_ERROR': {
          await dialog.alert({
            title,
            content: t('subscription:SpaceDetail.changeCardModal.reason', {
              message,
            }),
            useOutSideClick: false,
          });
          break;
        }
        case 'FAIL_TO_EDIT_ZIP_CODE': {
          await dialog.alert({
            title: t('subscription:errorModal.zipCode.title'),
            content: t('subscription:errorModal.zipCode.content'),
          });
          break;
        }
        case 'ZEP_API_ERROR': {
          await paymentRetryDialog.show({
            errorCode: message,
            onRetry: () => {
              retry();
            },
          });
          break;
        }
        case 'UNKNOWN_ERROR': {
          await dialog.alert({
            title,
            content: 'UNKNOWN_ERROR',
            useOutSideClick: false,
          });
        }
      }

      console.error(e);
      /** 신규등록일 경우에만 카드정보를 삭제후 새롭게 요청한다 */
      if (!isUpdated && useRemoveBillingKey) {
        removePaymentCard(spaceHashId);
      }

      throw new Error('SubscriptionPlan');
    }
  };

  return {
    subscribePlan,
  };
};
